/**
 * 67.78 (the owner: "Ok another item, ciws sound"; then "CIWs sound is good, I think rounds should self destruct after some
 * time as it will look cool", and "Also we can re-use that for later bushmaster type stuff"): how an autocannon is heard —
 * the rules; `autocannonSound.ts` voices them. A gun is an entry in `CANNON_VOICES`, keyed by gun, the way the dropship's
 * voice is a table (dropshipMix `hearDropship`): its rate and calibre's pitch, its reach, its rotary spin (or none) and
 * its self-destruct (or none). 69.9's two base mounts are its first entries — both fire the M61's 20 mm six-barrel rotary
 * cannon (the real CIWS and its land-based C-RAM, 75 rounds a second); a vehicle's chain gun (a Bushmaster-type 25–30 mm
 * on an IFV) would be another, with nothing here tied to point defence:
 *   - **the gun:** the rounds' cracks fused into the rate's tearing sound (a crack a round, the growl at the rate's pitch
 *     under it, the muzzle blast's roar), heard like the .50 but louder and further (`range`), late by the speed of sound,
 *     its echo rolling on after a burst stops;
 *   - **firing** is a gate on the host's bursts: the sim fires a burst every `interval` while it engages (point defence's
 *     `pdBurst`: the CIWS every 0.15 s, the C-RAM every 0.25 s), so the gun sounds from a burst until none has come for
 *     `gapShare` × the interval — a continuous burst for as long as it engages;
 *   - **the spin** (a rotary gun): the barrel cluster's motor whine, spinning up while it tracks (point defence's
 *     `pd.tracks`: the sim's track time before the first burst is the spin-up) or fires, winding down after;
 *   - **the self-destruct** (the owner: "rounds should self destruct after some time"): a missed tracer flies on and bursts
 *     after a fuse of `fuse` seconds (spread over the range so a burst's rounds ripple), a small airburst drawn where it
 *     ends and its pop heard from there (`cannonFuses`, `hearCannonPop`) — the presentation's choice of rounds and times,
 *     not the sim's.
 * Pure: no audio here, no state written. Unplayed guesses (decision 121): the owner's ear decides.
 */
import { POINT_DEFENCE } from '../constants/action';
import { METRES_PER_PERSON, SPEED_OF_SOUND } from './gunfireMix';

/**
 * An autocannon's voice:
 *   - `rate` rounds a second; the cracks' band (`crackHz`, the calibre's pitch: a bigger round lower), the growl's level, the
 *     roar's band;
 *   - `gain` against the rifle's report at the gun, `range` × a shot's reach (6 people) and range (700 people), the echo's
 *     length (s);
 *   - `rotary`: the barrels' whine at rest and full (Hz), its level, the spin-up and wind-down (s); null for a chain gun;
 *   - `interval`: the host's burst cadence (s), and `gapShare` × it after the last burst it has stopped;
 *   - `selfDestruct`: how many of a missed burst's tracers burst (`count`), after a fuse of `fuse` seconds, the pop's
 *     loudness near (`level`), halved by `reach` people, heard to `range`; null when its rounds do not self-destruct.
 */
export interface CannonVoice {
  rate: number; crackHz: number; growl: number; roarHz: number;
  gain: number; range: number; tail: number;
  rotary: { whineRest: number; whineFull: number; whine: number; spinUp: number; spinDown: number } | null;
  interval: number; gapShare: number;
  selfDestruct: { count: number; fuse: readonly [number, number]; level: number; reach: number; range: number } | null;
}
/** The guns heard so far: 69.9's base mounts (point defence's variants). A vehicle's autocannon would add its own key. */
export type CannonKey = 'ciws' | 'cram';

const M61 = { rate: 75, crackHz: 1400, growl: 0.55, roarHz: 650, gain: 1.3, range: 2.2, tail: 1.8,
  rotary: { whineRest: 280, whineFull: 1150, whine: 0.07, spinUp: 0.6, spinDown: 1.6 }, gapShare: 1.6 } as const;
/**
 * The entries. Both mounts carry the M61 (one sound); they differ in the sim's cadence and in how many of a miss's tracers
 * self-destruct where the eye catches them — the C-RAM's slower bursts three, the CIWS's quicker ones two, each 0.8–1.4 s
 * out (360–630 people at the tracers' 450 a second: far past the target, the pops rippling as the rounds' fuses run out).
 */
export const CANNON_VOICES: Record<CannonKey, CannonVoice> = {
  ciws: { ...M61, interval: POINT_DEFENCE.variants.ciws.interval, selfDestruct: { count: 2, fuse: [0.8, 1.4], level: 0.9, reach: 25, range: 900 } },
  cram: { ...M61, interval: POINT_DEFENCE.variants.cram.interval, selfDestruct: { count: 3, fuse: [0.8, 1.4], level: 0.9, reach: 25, range: 900 } },
};

const clamp = (x: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, x));

/** A gun as heard `people` away on its `pan` side: loudness, the air's cut, the delay, the side, the echo (null past its range). */
export interface CannonHeard { gain: number; cutoff: number; delay: number; pan: number; tail: number }
export function hearCannon(V: CannonVoice, people: number, pan: number, env = 0): CannonHeard | null {
  const d = Math.max(0, people);
  if (d > 700 * V.range) return null;
  return {
    gain: V.gain / (1 + d / (6 * V.range)),
    cutoff: clamp(14000 * Math.exp(-d / (220 * V.range)), 700, 14000) * (1 - 0.35 * clamp(env, 0, 1)),
    delay: (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: V.tail * (1 + d / 400) * (1 + 0.6 * clamp(env, 0, 1)),
  };
}

/** Is the gun firing at `seconds`, its last burst at `lastBurst` (seconds; null or undefined: none yet)? Until the gap has passed. */
export function cannonFiring(V: CannonVoice, lastBurst: number | null | undefined, seconds: number): boolean {
  return lastBurst != null && seconds >= lastBurst && seconds - lastBurst <= V.interval * V.gapShare;
}
/** The barrels' spin to aim for: up while it tracks or fires, else down. */
export function cannonSpinTarget(tracking: boolean, firing: boolean): number { return tracking || firing ? 1 : 0; }
/** The spin `dt` seconds on toward `target` (0 … 1): up over `spinUp`, down over `spinDown`; a chain gun (no rotary) at its target at once. */
export function cannonSpin(V: CannonVoice, spin: number, target: number, dt: number): number {
  const R = V.rotary;
  if (!R) return target;
  return target > spin ? Math.min(target, spin + dt / R.spinUp) : Math.max(target, spin - dt / R.spinDown);
}
/** The whine's pitch and level share at `spin` (none without a rotary), and the rate the rounds come at (barrels still spinning up fire slower). */
export function cannonWhine(V: CannonVoice, spin: number): { hz: number; level: number; rate: number } {
  const s = clamp(spin, 0, 1), R = V.rotary;
  if (!R) return { hz: 0, level: 0, rate: V.rate };
  return { hz: R.whineRest + (R.whineFull - R.whineRest) * s, level: R.whine * s * s, rate: V.rate * (0.55 + 0.45 * s) };
}

/**
 * A missed burst's self-destructing tracers: of `tracers` drawn, which burst and after what fuse (s), each fuse drawn from
 * the gun's span by `rnd` (0 … 1; the host's own randomness — presentation, not the sim's) and spread across it, so the
 * rounds ripple out rather than flash at once. None for a hit or a gun whose rounds do not self-destruct.
 */
export function cannonFuses(V: CannonVoice, hit: boolean, tracers: number, rnd: () => number): Array<{ tracer: number; fuse: number }> {
  const S = V.selfDestruct;
  if (hit || !S) return [];
  const n = Math.min(S.count, tracers), out: Array<{ tracer: number; fuse: number }> = [];
  const pick = Math.floor(rnd() * tracers);
  for (let i = 0; i < n; i++) {
    const slot = (i + rnd() * 0.9) / n;   // (spread across the span: one a slot)
    out.push({ tracer: (pick + i) % tracers, fuse: S.fuse[0] + (S.fuse[1] - S.fuse[0]) * slot });
  }
  return out;
}
/** A self-destruct pop `people` away (null past its range, or for a gun without one). */
export function hearCannonPop(V: CannonVoice, people: number, pan: number): CannonHeard | null {
  const S = V.selfDestruct, d = Math.max(0, people);
  if (!S || d > S.range) return null;
  return {
    gain: S.level / (1 + d / S.reach),
    cutoff: clamp(14000 * Math.exp(-d / 260), 700, 14000),
    delay: (d * METRES_PER_PERSON) / SPEED_OF_SOUND,
    pan: clamp(pan, -1, 1),
    tail: 0.6,
  };
}
